import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ShieldAlert } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/session", null, {
          headers: { "X-Session-ID": sessionId },
        });
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true, state: { user: data } });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const denied = e?.response?.status === 403;
        setError(detail || "Sign-in failed. Please try again.");
        if (!denied) setTimeout(() => navigate("/", { replace: true }), 1800);
      }
    })();
  }, [navigate, setUser]);

  const denied = !!error && error.startsWith("Access denied");

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="text-center max-w-md">
        {denied ? (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <ShieldAlert className="h-6 w-6 text-destructive" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold" data-testid="access-denied-title">Access denied</h2>
            <p className="mt-2 text-muted-foreground text-sm" data-testid="access-denied-message">{error}</p>
            <button data-testid="back-to-signin" onClick={() => navigate("/", { replace: true })} className="mt-6 inline-flex items-center rounded-lg bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90">
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">{error || "Signing you in…"}</p>
          </>
        )}
      </div>
    </div>
  );
}
